import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Loader2, X, Store } from 'lucide-react';
import { DealCard } from '../components/DealCard';
import { Button } from '../components/ui/button';
import { API } from '../config/apiBase';
import { montaBuscaParams } from '../utils/buscaParams';
import { novoId, registrar } from '../inteligencia/coleta';

// A URL e a unica fonte de verdade dos filtros: `q` e `marketplace` moram nela e
// nada e espelhado em estado paralelo. E o que faz o link do card de loja da
// Central (`/busca?marketplace=<slug>`) ser compartilhavel e voltar igual.
//
// O slug do marketplace NAO e traduzido aqui de proposito. O nome bonito vive em
// MARKETPLACE_PROVIDERS, no backend, e a rota que o expoe e admin-only. Copiar a
// tabela para ca criaria uma segunda taxonomia, que e exatamente o defeito que
// esta rodada foi feita para nao repetir.

export default function SearchPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    // Liga a busca ao clique no resultado (inteligência v2). Um id por resposta recebida.
    const [buscaId, setBuscaId] = useState('');

    const searchQuery = searchParams.get('q') || '';
    const marketplace = searchParams.get('marketplace') || '';

    const [query, setQuery] = useState(searchQuery);

    // O campo de texto acompanha a URL quando ela muda por fora (voltar, link
    // colado, clique num card de loja).
    useEffect(() => { setQuery(searchQuery); }, [searchQuery]);

    useEffect(() => {
        if (!searchQuery && !marketplace) {
            setDeals([]);
            setTotal(0);
            return undefined;
        }
        let ativo = true;
        setLoading(true);

        // Parametro vazio nao entra na query string. Ver utils/buscaParams.js.
        const params = montaBuscaParams({ q: searchQuery, marketplace });

        fetch(`${API}/produtos?${params.toString()}`)
            .then((r) => r.json())
            .then((d) => {
                if (!ativo) return;
                const lista = Array.isArray(d?.deals) ? d.deals : [];
                const n = typeof d?.total === 'number' ? d.total : 0;
                setDeals(lista);
                setTotal(n);
                const id = novoId('bus');
                setBuscaId(id);
                if (searchQuery) registrar('search', { query: searchQuery, result_count: n, store: marketplace, search_id: id });
            })
            .catch(() => {
                // Sem resultado parcial: erro limpa a lista em vez de deixar na
                // tela o resultado de uma busca anterior.
                if (!ativo) return;
                setDeals([]);
                setTotal(0);
            })
            .finally(() => { if (ativo) setLoading(false); });

        // Resposta de uma busca abandonada nao sobrescreve a atual.
        return () => { ativo = false; };
    }, [searchQuery, marketplace]);

    const handleSearch = (e) => {
        e.preventDefault();
        const proximo = {};
        const termo = query.trim();
        if (termo) proximo.q = termo;
        // Buscar por texto NAO descarta a loja: quem chegou por um card de loja
        // continua dentro dela ao refinar a busca.
        if (marketplace) proximo.marketplace = marketplace;
        setSearchParams(proximo);
    };

    const limparTermo = () => {
        setQuery('');
        const proximo = {};
        if (marketplace) proximo.marketplace = marketplace;
        setSearchParams(proximo);
    };

    const limparLoja = () => {
        registrar('filter', { filter_type: 'store', filter_value: 'none' });
        const proximo = {};
        if (searchQuery) proximo.q = searchQuery;
        setSearchParams(proximo);
    };

    const temFiltro = Boolean(searchQuery || marketplace);
    let titulo = '';
    if (loading) {
        titulo = 'Buscando...';
    } else if (temFiltro) {
        const alvo = searchQuery ? `"${searchQuery}"` : 'esta loja';
        titulo = total > 0
            ? `${total} resultado${total !== 1 ? 's' : ''} para ${alvo}`
            : `Nenhum resultado para ${alvo}`;
    }

    return (
        <div className="min-h-screen" style={{ background: 'var(--bg)' }} data-testid="search-page">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Search Header */}
                <div className="mb-8">
                    <form onSubmit={handleSearch} className="relative">
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--muted-2)' }} />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Buscar ofertas, lojas, produtos..."
                            className="search-input w-full rounded-full pl-14 pr-32 py-4 text-lg"
                            data-testid="search-page-input"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                            {query && (
                                <button
                                    type="button"
                                    onClick={limparTermo}
                                    className="p-2 transition-colors"
                                    style={{ color: 'var(--muted-2)' }}
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            )}
                            <Button type="submit" className="btn-gold rounded-full px-6" data-testid="search-submit">
                                Buscar
                            </Button>
                        </div>
                    </form>
                </div>

                {/* Filtro de loja ativo */}
                {marketplace && (
                    <div className="mb-4 flex items-center gap-2">
                        <span
                            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full"
                            style={{ background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }}
                            data-testid="search-marketplace-chip"
                        >
                            <Store className="w-4 h-4" style={{ color: 'var(--gold-2)' }} />
                            Loja: {marketplace}
                            <button
                                type="button"
                                onClick={limparLoja}
                                aria-label="Remover filtro de loja"
                                className="ml-1"
                                style={{ color: 'var(--muted-2)' }}
                                data-testid="search-marketplace-clear"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </span>
                    </div>
                )}

                {/* Results */}
                {temFiltro && (
                    <div className="mb-6">
                        <h1 className="text-xl font-outfit font-bold" style={{ color: 'var(--text)' }}>
                            {titulo}
                        </h1>
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
                    </div>
                ) : deals.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {deals.map((deal, index) => (
                            <div key={deal.deal_id} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                                <DealCard deal={deal} exposicao={{ surface: 'search_results', position: index + 1, section: marketplace ? 'loja' : 'busca', searchId: buscaId }} />
                            </div>
                        ))}
                    </div>
                ) : temFiltro ? (
                    <div className="text-center py-20">
                        <p className="mb-4" style={{ color: 'var(--muted-2)' }}>Nenhuma oferta encontrada para sua busca.</p>
                        <Link to="/">
                            <Button variant="outline" className="border-gold-500/50 text-gold-500">
                                Ver todas as ofertas
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="text-center py-20">
                        <Search className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--muted-2)' }} />
                        <p style={{ color: 'var(--muted-2)' }}>Digite sua busca acima para encontrar ofertas</p>
                    </div>
                )}
            </div>
        </div>
    );
}
